import type { AuthUser, DashboardSummary } from "@apple-bee/contracts";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowUpRight,
  Boxes,
  ClipboardCheck,
  PackageCheck,
  Scale,
} from "lucide-react";
import { api } from "../api";
import { EmptyState, LoadingBlock } from "../components/Feedback";
import { dateTimeFormatter, formatKg } from "../format";

export function OverviewPage({ user }: { user: AuthUser }) {
  const query = useQuery({
    queryKey: ["dashboard"],
    queryFn: () =>
      api<{ summary: DashboardSummary }>("/api/dashboard/summary"),
  });

  if (query.isLoading) return <LoadingBlock label="Собираем сводку…" />;
  if (!query.data) {
    return (
      <EmptyState
        title="Сводка пока недоступна"
        description="Повторите попытку после проверки соединения с сервером."
      />
    );
  }
  const summary = query.data.summary;

  return (
    <div className="page-stack">
      <section className="welcome-banner">
        <div>
          <p className="eyebrow eyebrow--light">Сегодня в Apple Bee</p>
          <h2>Здравствуйте, {user.displayName.split(/\s+/u)[0]}</h2>
          <p>
            Здесь отображаются только подтверждённые сервером остатки и заявки.
          </p>
        </div>
        <div className="welcome-banner__seal">
          <span>AB</span>
          <small>операционный<br />контур</small>
        </div>
      </section>

      <section className="metric-grid" aria-label="Ключевые показатели">
        <Metric
          icon={Scale}
          label="Физический остаток"
          value={formatKg(summary.totalStockKg)}
          hint="По всем активным складам"
          tone="green"
        />
        <Metric
          icon={Boxes}
          label="Активные партии"
          value={String(summary.activeLots)}
          hint="С ненулевым остатком"
          tone="amber"
        />
        <Metric
          icon={ClipboardCheck}
          label="Ждут решения CEO"
          value={String(summary.pendingShipments)}
          hint="Заявки на отгрузку"
          tone="red"
        />
        <Metric
          icon={PackageCheck}
          label="Отгружено сегодня"
          value={formatKg(summary.shippedToday)}
          hint="По проведённым отгрузкам"
          tone="blue"
        />
      </section>

      <section className="overview-grid">
        <article className="panel">
          <header className="panel__header">
            <div>
              <p className="eyebrow">Контроль остатков</p>
              <h2>Недавние изменения</h2>
            </div>
            <span className="panel__meta">Последние 5</span>
          </header>
          {summary.recentMovements.length ? (
            <div className="activity-list">
              {summary.recentMovements.map((row) => (
                <div className="activity-row" key={row.balanceId}>
                  <span className="activity-row__icon">
                    <ArrowUpRight size={17} />
                  </span>
                  <div>
                    <strong>{row.productName}</strong>
                    <span>
                      {row.lotCode} · {row.warehouseName}
                    </span>
                  </div>
                  <div className="activity-row__value">
                    <strong>{formatKg(row.quantityKg)}</strong>
                    <span>{dateTimeFormatter.format(new Date(row.updatedAt))}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              title="Движений ещё нет"
              description="После первого прихода здесь появится оперативная картина."
            />
          )}
        </article>

        <article className="panel">
          <header className="panel__header">
            <div>
              <p className="eyebrow">Требует внимания</p>
              <h2>Низкий свободный остаток</h2>
            </div>
            <AlertTriangle size={20} className="text-amber" />
          </header>
          {summary.lowStockItems.length ? (
            <div className="risk-list">
              {summary.lowStockItems.map((item) => (
                <div
                  className="risk-row"
                  key={`${item.productName}-${item.warehouseName}`}
                >
                  <span className="risk-row__dot" />
                  <div>
                    <strong>{item.productName}</strong>
                    <span>{item.warehouseName}</span>
                  </div>
                  <strong>{formatKg(item.availableKg)}</strong>
                </div>
              ))}
            </div>
          ) : (
            <div className="all-clear">
              <PackageCheck size={28} />
              <div>
                <strong>Критичных позиций нет</strong>
                <span>Все остатки выше заданных порогов.</span>
              </div>
            </div>
          )}
        </article>
      </section>
    </div>
  );
}

function Metric({
  icon: Icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: typeof Scale;
  label: string;
  value: string;
  hint: string;
  tone: "green" | "amber" | "red" | "blue";
}) {
  return (
    <article className={`metric-card metric-card--${tone}`}>
      <span className="metric-card__icon">
        <Icon size={21} />
      </span>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{hint}</small>
      </div>
    </article>
  );
}

